import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import AppButton from "./AppButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import ImagePreviewModal from "./ImagePreviewModal";

/**
 * UploadPictures - Reusable component for uploading, displaying, and previewing images
 * @param {Object} props - Component props
 * @param {boolean} props.viewOnly - Whether the component is in view-only mode
 * @param {Array} props.pictures - Array of picture objects with id, file, and url properties
 * @param {Function} props.onChange - Callback function when pictures are added or removed
 * @param {string} props.label - Label for the upload button section
 * @param {number} props.labelWidth - Width of the label in pixels
 * @param {number} props.maxImages - Maximum number of images allowed (default: 5)
 */
const UploadPictures = ({
  viewOnly = false,
  pictures = [],
  onChange = () => {},
  label = "Upload Pictures",
  labelWidth = 160,
  maxImages = 5,
}) => {
  const fileInputRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // Normalize pictures for rendering: support both local uploads and server images
  const normalizedPictures = Array.isArray(pictures)
    ? pictures.filter(Boolean).map((pic, idx) => {
        const url =
          pic.url ||
          (pic.image_url && baseUrl + pic.image_url) ||
          (pic.file && pic.file.url) ||
          "";
        const id = pic.id ?? pic.image_id ?? `${idx}`;
        const name =
          (pic.file && pic.file.name) || pic.name || `image_${idx + 1}`;
        return { id, url, name };
      })
    : [];

  // Handle picture upload
  const handlePictureUpload = (e) => {
    if (viewOnly) return;

    if (e.target.files) {
      // Check if adding new files would exceed the maximum
      const remainingSlots = maxImages - pictures.length;

      if (remainingSlots <= 0) {
        alert(`Maximum of ${maxImages} images allowed.`);
        e.target.value = null;
        return;
      }

      // Only take as many files as we have slots for
      const filesToAdd = Array.from(e.target.files).slice(0, remainingSlots);

      const newFiles = filesToAdd.map((file) => ({
        file,
        id: `${file.name}-${Date.now()}`,
        url: URL.createObjectURL(file),
      }));

      const updatedPictures = [...pictures, ...newFiles];

      // Update parent component state
      onChange(updatedPictures);

      // Alert user if some files were not added due to the limit
      if (e.target.files.length > remainingSlots) {
        alert(
          `Only added ${remainingSlots} image(s). Maximum of ${maxImages} images allowed.`
        );
      }
    }
    e.target.value = null;
  };

  // Handle picture deletion
  const handleDeletePicture = (indexToDelete) => {
    if (viewOnly) return;

    const updatedPictures = pictures.filter((_, idx) => idx !== indexToDelete);

    // Update parent component state
    onChange(updatedPictures);
  };

  // Handle image preview
  const handleOpenPreview = (index) => {
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  return (
    <>
      {/* Upload Pictures */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {label && (
          <Typography variant="body2" sx={{ minWidth: labelWidth }}>
            {label}
          </Typography>
        )}
        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handlePictureUpload}
          />
          <AppButton
            variant="contained"
            buttonType="main"
            component="label"
            label="Upload"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current.click()}
            disabled={viewOnly}
          />
          <Typography variant="caption" sx={{ ml: 2 }}>
            {normalizedPictures.length} / {maxImages} image
            {normalizedPictures.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </Box>

      {/* Image thumbnails display */}
      {normalizedPictures.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <Box sx={{ minWidth: labelWidth }} />
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              maxWidth: "100%",
              my: 1,
            }}
          >
            {normalizedPictures.map((pic, index) => (
              <Box
                key={pic.id}
                sx={{
                  position: "relative",
                  width: 100,
                  height: 100,
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  overflow: "hidden",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  "&:hover": {
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    transform: "scale(1.02)",
                    transition: "all 0.2s ease-in-out",
                  },
                  cursor: "pointer",
                }}
              >
                {!viewOnly && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(0,0,0,0.5)",
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 2,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePicture(index);
                    }}
                  >
                    <CloseIcon sx={{ color: "#fff", fontSize: 16 }} />
                  </Box>
                )}
                <Box
                  component="img"
                  src={pic.url}
                  alt={`Uploaded ${pic.name}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onClick={() => handleOpenPreview(index)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal
        open={previewOpen}
        onClose={handleClosePreview}
        images={normalizedPictures.map((pic) => pic.url)}
        initialIndex={previewIndex}
      />
    </>
  );
};

export default UploadPictures;
